<!-- TABLE/LIST VIEW -->
<section
  *ngIf="(configurationsState$ | async) === 'table'"
  class="fx-col fx-gap-3">

  <!-- Header with refresh button -->
  <div class="fx-row fx-justify-between fx-align-center w-full">
    <lib-block-title
      id="devices-title"
      [title]="labels.tableTitle">
    </lib-block-title>
    <button
      type="button"
      mat-button
      class="custom-mat-button-primary ml-0 custom-mat-button-border"
      [disabled]="isLoadingTable$ | async"
      (click)="manualRefresh()">
      <mat-icon class="m-0" [class.spin]="isLoadingTable$ | async">refresh</mat-icon>
    </button>
  </div>

  <!-- Desktop Table -->
  <azzurro-table
    id="devices-table"
    [uiFramework]="'material'"
    [columns]="tableColumns"
    [data$]="tableData$"
    [mode]="'client'"
    [pagination]="false"
    [noResultTemplate]="noDevicesTemplate">
  </azzurro-table>

  <!-- Mobile List -->
  <azzurro-table
    id="devices-list"
    [uiFramework]="'ionic'"
    [columns]="tableColumns"
    [data$]="tableData$"
    [mode]="'client'"
    [pagination]="false"
    [noResultTemplate]="noDevicesTemplate">
  </azzurro-table>
</section>

<!-- UPGRADE VIEW -->
<section
  *ngIf="(configurationsState$ | async) === 'upgrade'"
  class="fx-col fx-gap-3">
  <div class="fx-row fx-justify-between fx-g-1 fx-align-start">
    <button
      type="button"
      mat-button
      class="custom-mat-button-primary ml-0"
      (click)="goBackToTable()">
      {{ labels.back }}
    </button>
    <lib-block-title [title]="(selectedInverter$ | async) ?? ''">
    </lib-block-title>
  </div>
  <!-- Hide info section when device is not supported -->
  <section
    *ngIf="!isSelectedDeviceNotSupported"
    class="border-1 fx-center fx-gap-3 p-1 radius-4 radius-2 border-grey-100">
    <mat-icon class="text-orange-400 min-s-6 s-6">
      info
    </mat-icon>
    <customer-title-content
      class="fx-g-1"
      [content]="labels.upgradeTitleContent">
    </customer-title-content>
  </section>
  <lib-upgrade-inverter
    *ngIf="selectedInverter$ | async as inverterSn"
    [refresh$]="refresh$"
    [labels]="labels"
    [inverterSn]="inverterSn"
    [system]="system"
    [isAzzurroHub]="isAzzurroHub"
    [gatewaySN]="gatewaySN"
    (deviceNotSupportedChange)="onDeviceNotSupportedChange($event)">
  </lib-upgrade-inverter>
</section>

<!-- CONFIG VIEW -->
<section
  *ngIf="(configurationsState$ | async) === 'config'"
  class="fx-col fx-gap-3">
  <div class="fx-row fx-justify-between fx-g-1 fx-align-start">
    <button
      type="button"
      mat-button
      class="custom-mat-button-primary ml-0"
      (click)="goBackToTable()">
      {{ labels.back }}
    </button>
    <lib-block-title [title]="(selectedInverter$ | async) ?? ''">
    </lib-block-title>
  </div>
  <lib-settings-inverter
    *ngIf="selectedInverter$ | async as inverterSn"
    [labels]="labels"
    [inverterId]="inverterSn"
    [channelType]="channelType"
    [gatewaySN]="gatewaySN"
    [modbusAddress]="selectedModbusAddress"
    (fatalError)="goBackToTable()">
  </lib-settings-inverter>
</section>

<!-- Templates for azzurro-table -->

<!-- No devices template -->
<ng-template #noDevicesTemplate>
  <div class="p-3 text-center text-grey-400 w-full bg-grey-100 border-1 radius-4 border-grey-200">
    {{ labels.noDevices }}
  </div>
</ng-template>

<!-- Status template (Online/Offline/Unknown) -->
<ng-template #statusTemplate let-row>
  <ng-container *ngIf="!row.isLoading; else loadingCell">
    <span
      class="status-badge"
      [class.status-online]="row.isOnline === true"
      [class.status-offline]="row.isOnline === false"
      [class.status-unknown]="row.isOnline === null">
      <span class="status-dot"></span>
      {{ row.isOnline === true ? labels.online : (row.isOnline === false ? labels.offline : labels.unknown) }}
    </span>
  </ng-container>
</ng-template>

<!-- Logger status template -->
<ng-template #loggerStatusTemplate let-row>
  <ng-container *ngTemplateOutlet="moduleStatusCell; context: {
    status: row.loggerStatus,
    version: row.loggerCurrentVersion,
    isLoading: row.isLoading
  }"></ng-container>
</ng-template>

<!-- ARM status template -->
<ng-template #armStatusTemplate let-row>
  <ng-container *ngTemplateOutlet="moduleStatusCell; context: {
    status: row.armStatus,
    version: row.armCurrentVersion,
    isLoading: row.isLoading
  }"></ng-container>
</ng-template>

<!-- DSPM status template -->
<ng-template #dspmStatusTemplate let-row>
  <ng-container *ngTemplateOutlet="moduleStatusCell; context: {
    status: row.dspmStatus,
    version: row.dspmCurrentVersion,
    isLoading: row.isLoading
  }"></ng-container>
</ng-template>

<!-- DSPS status template -->
<ng-template #dspsStatusTemplate let-row>
  <ng-container *ngTemplateOutlet="moduleStatusCell; context: {
    status: row.dspsStatus,
    version: row.dspsCurrentVersion,
    isLoading: row.isLoading
  }"></ng-container>
</ng-template>

<!-- Helper template for module status cell rendering -->
<ng-template
  #moduleStatusCell
  let-status="status"
  let-version="version"
  let-isLoading="isLoading">
  <ng-container *ngIf="!isLoading; else loadingCell">
    <ng-container [ngSwitch]="status">
      <!-- Updated -->
      <span
        *ngSwitchCase="'updated'"
        class="module-status status-updated"
        [matTooltip]="version || ''">
        <mat-icon class="s-5">check_circle</mat-icon>
      </span>

      <!-- Needs Update -->
      <span
        *ngSwitchCase="'needs_update'"
        class="module-status status-needs-update"
        [matTooltip]="version || ''">
        <mat-icon class="s-5">warning</mat-icon>
      </span>

      <!-- Unknown (version mismatch - current version different from expected) -->
      <span
        *ngSwitchCase="'unknown'"
        class="module-status status-version-mismatch"
        [matTooltip]="version || ''">
        <mat-icon class="s-5">warning</mat-icon>
      </span>

      <!-- Not Applicable -->
      <span
        *ngSwitchCase="'not_applicable'"
        class="module-status status-na">
        <span class="text-grey-300">-</span>
      </span>

      <!-- Error -->
      <span
        *ngSwitchCase="'error'"
        class="module-status status-error"
        [matTooltip]="labels.error">
        <mat-icon class="s-5">error</mat-icon>
      </span>

      <!-- Loading -->
      <span *ngSwitchCase="'loading'" class="module-status">
        <ion-skeleton-text
          animated="true"
          class="h-5 w-5">
        </ion-skeleton-text>
      </span>

      <!-- Default -->
      <span *ngSwitchDefault class="module-status status-na">
        <span class="text-grey-300">-</span>
      </span>
    </ng-container>
  </ng-container>
</ng-template>

<!-- Loading cell helper -->
<ng-template #loadingCell>
  <ion-skeleton-text
    animated="true"
    class="h-6 w-16 mx-auto">
  </ion-skeleton-text>
</ng-template>

<!-- Actions template -->
<ng-template #actionsTemplate let-row>
  <div class="fx-row fx-center fx-gap-1 ml-auto">
    <!-- Upgrading badge (shown alongside upgrade button when AH upgrade is active) -->
    <div *ngIf="row.isUpgrading" class="upgrading-indicator fx-row fx-center fx-gap-2">
      <mat-icon class="text-accent spin s-5">sync</mat-icon>
      <span class="text-accent text-sm tw-font-medium">{{ labels.upgrading }}</span>
    </div>

    <!-- Not-certified warning -->
    <mat-icon
      *ngIf="row.isCertified === false"
      class="text-orange s-5"
      [matTooltip]="labels.notCertifiedTooltip">
      warning
    </mat-icon>

    <!-- Upgrade button (always visible, even during upgrade — allows re-entering upgrade view) -->
    <button
      type="button"
      mat-icon-button
      class="action-button"
      [matTooltip]="labels.upgrade"
      [disabled]="!row.canUpgrade && !row.isUpgrading"
      (click)="goToUpgrade(row.serialNumber)">
      <mat-icon
        [class.text-grey-300]="!row.canUpgrade && !row.isUpgrading"
        [class.text-accent]="row.canUpgrade || row.isUpgrading">
        system_update
      </mat-icon>
    </button>

    <!-- Configure button (disabled during upgrade) -->
    <button
      type="button"
      mat-icon-button
      class="action-button"
      [matTooltip]="labels.configure"
      [disabled]="!row.canConfigure || row.isUpgrading"
      (click)="goToConfig(row.serialNumber)">
      <mat-icon
        [class.text-grey-300]="!row.canConfigure || row.isUpgrading"
        [class.text-accent]="row.canConfigure && !row.isUpgrading">
        settings
      </mat-icon>
    </button>
  </div>
</ng-template>
